import * as PIXI from 'pixi.js';
import { AssetLoader } from './assetLoader';
import { SpineLoader } from './spineLoader';
import { SpineObject } from './SpineObject';
import { UIButtonManager } from './UIButtonManager';
import { SymbolManager } from './symbolManager';
import { soundManager } from './soundManager';
import { languageManager } from './languageManager';
import { initializeNetworkService, getPendingBetScores, handleCoinPlayGame } from './networkService';
import { getUserDetails } from '../../../apis/user.services';

export class Game {
  constructor(app, assetLoader) {
    this.app = app;
    this.assetLoader = assetLoader;
    this.initGame();
    this.spineLoader = new SpineLoader();
    this.bg = null;
    this.reelFrame = null;
    this.spineObjects = new Map();
    this.gameLoop = null;
    this.credits = 100;
    this.wallet = {};
    this.userDetail = {}

    this.app.stage.sortableChildren = true;
    // Initialize language manager
    this.languageManager = languageManager;

    this.gameContainer = new PIXI.Container();
    this.gameContainer.zIndex = 10; // Game layer
    this.app.stage.addChild(this.gameContainer);
    
    // The UIButtonManager will create its own uiContainer which we will manage
    this.buttonManager = null; 

    window.addEventListener('resize', this.onResize.bind(this));
  }

  async initGame() {
    // ... other initialization tasks
    await initializeNetworkService(); // This will trigger the pending bet check
    // ... rest of game initialization after network service is ready
  }

  async getUserDetail() {
    try {
      const res = await getUserDetails();
      const wallet = res?.user?.wallets?.filter((item) => item?.isDefault)?.[0]
      const balance = wallet?.amount + wallet?.bonusAmount + wallet?.withdrawableAmount
      this.userDetail = res
      this.credits = typeof balance === 'number' && !isNaN(balance) ? balance : 0;
      this.wallet = wallet

      console.log(this.userDetail, 'this.userDetail')
      console.log('userDetail in game', res);
    } catch (err) {
      console.error('Failed to submit win:', err);
      this.credits = 0;
    }
  }

  async initialize() {
    await this.getUserDetail();
    this.loadBackground();
    this.loadReelFrame();
    await this.loadSpineAssets();
    const gameData = await getPendingBetScores();
    if (gameData.wallet !== undefined && gameData.wallet !== null) {
        this.setCredits(gameData.wallet); // Update credits from pending bet API
    }

    // gameData.scores = [4,500,25,20,50,15,100,5,40]
    // gameData.baseScorePattern = [3,10]

    if (this.symbolManager) {
      if(gameData.scores.length > 0) {
        this.symbolManager.addActualSymbols(gameData);
        if(this.buttonManager) {
          this.buttonManager.setGameContinuationUI(true);
        }
      } else {
        // New player, no pending bet. Do not add symbols yet.
        // The UI should be set up to prompt the user to buy a ticket.
        console.log("No pending bet scores found. Waiting for ticket purchase.");
        if(this.buttonManager) {
          this.buttonManager.setGameContinuationUI(false);
        }
      }   
    }
    this.loadUIBase(gameData);
    this.createSpineObjects();
    if (this.buttonManager && this.buttonManager.uiContainer) {
      this.app.stage.addChild(this.buttonManager.uiContainer);
    }
    this.startGameLoop();
  }

  loadBackground() {
    const texture = PIXI.Texture.from('/assets/pixi-scratch/Background/basegame_background.webp');
    this.bg = PIXI.Sprite.from(texture);
    this.bg.x = 0;
    this.bg.y = 0;
    this.bg.width = this.app.screen.width;
    this.bg.height = this.app.screen.height;
    this.app.stage.addChild(this.bg);
    if (this.buttonManager && this.buttonManager.uiContainer) {
      this.app.stage.addChild(this.buttonManager.uiContainer);
    }
  }


  loadReelFrame() {
    const sheet = this.assetLoader.getSheet('basegame');
    if (sheet) {
      this.reelFrame = PIXI.Sprite.from(sheet.textures['reel_frame.png']);

      // Mobile-responsive scaling - zoom out based on screen size
      const screenWidth = this.app.screen.width;
      const screenHeight = this.app.screen.height;

      // Calculate scale based on screen size
      let scale = 0.6; // Default scale
      if (screenWidth <= 480) {
        scale = 0.33; // Small mobile - zoom out more
      } else if (screenWidth <= 767) {
        scale = 0.45; // Regular mobile - zoom out
      } else if (screenWidth <= 1024) {
        scale = 0.5; // Tablet - slight zoom out
      } else {
        scale = 0.6; // Desktop - original scale
      }

      this.reelFrame.scale.set(scale);
      this.centerReelFrame();
      this.app.stage.addChild(this.reelFrame);
      if (this.buttonManager && this.buttonManager.uiContainer) {
        this.app.stage.addChild(this.buttonManager.uiContainer);
      }

      const basegameSheet = this.assetLoader.getSheet('basegame');
      const matchingIconTxtSheet = this.assetLoader.getSheet('matchingicontxt');
      const symbolsSheet = this.assetLoader.getSheet('symbols');
      this.symbolManager = new SymbolManager(this.app, symbolsSheet, this.reelFrame, basegameSheet, matchingIconTxtSheet);
      this.app.symbolManager = this.symbolManager;
      this.symbolManager.createGrid();
    }
  }

  /**
   * Load all Spine assets
   */
  async loadSpineAssets() {
    try {
      await this.spineLoader.loadDefaultSpineAssets();
      console.log('All Spine assets loaded successfully');
    } catch (error) {
      console.error('Failed to load Spine assets:', error);
    }
  }

  /**
 * Create Spine objects and add them to the stage
 */
  createSpineObjects() {
    try {
      const screenWidth = this.app.screen.width;
      const screenHeight = this.app.screen.height;

      console.log('Screen dimensions:', screenWidth, screenHeight);

      const bgSpine = new SpineObject('bgSkeleton', 'bgAtlas', 0, 0);

      if (!bgSpine.spine || !bgSpine.spine.skeleton) {
        console.error('Failed to create BG spine object');
        return;
      }

      const bounds = bgSpine.spine.getLocalBounds();
      const scaleX = screenWidth / bounds.width;
      const scaleY = screenHeight / bounds.height;
      const scale = Math.max(scaleX, scaleY);

      bgSpine.spine.scale.set(scale);

      if (bgSpine.spine.anchor) {
        bgSpine.spine.anchor.set(0.5, 0.5);
        bgSpine.x = screenWidth / 2;
        bgSpine.y = screenHeight / 2;
      } else {
        const scaledWidth = bounds.width * scale;
        const scaledHeight = bounds.height * scale;
        bgSpine.x = (screenWidth - scaledWidth) / 2 - bounds.x * scale;
        bgSpine.y = (screenHeight - scaledHeight) / 2 - bounds.y * scale;
      }

      this.spineObjects.set('bg', bgSpine);
      this.app.stage.addChild(bgSpine);
      if (this.buttonManager && this.buttonManager.uiContainer) {
        this.app.stage.addChild(this.buttonManager.uiContainer);
      }
      if (this.buttonManager && typeof this.buttonManager.ensureUIOnTop === 'function') {
        this.buttonManager.ensureUIOnTop();
      }

      // Play animation
      const bgAnims = bgSpine.spine.state.data.skeletonData.animations.map(a => a.name);
      if (bgAnims.length > 0) {
        bgSpine.spine.state.setAnimation(0, bgAnims[0], true);
        console.log('Playing BG animation:', bgAnims[0]);
      }

      console.log('All Spine objects created successfully');

    } catch (error) {
      console.error('Failed to create Spine objects:', error);
      console.error('Error stack:', error.stack);
    }
  }

  createRevealFxSpine() {
    try {
      if (!this.spineLoader.isAssetLoaded('reveal_fx')) {
        console.error('Reveal FX Spine assets not loaded yet');
        return;
      }
      const revealFxSpine = new SpineObject(
        'reveal_fxSkeleton',
        'reveal_fxAtlas',
        this.app.screen.width / 2,
        this.app.screen.height / 2 + 200
      );
      if (!revealFxSpine.spine || !revealFxSpine.spine.skeleton) {
        console.error('Failed to create Reveal FX spine object');
        return;
      }
      revealFxSpine.spine.scale.set(0.5);
      revealFxSpine.spine.anchor && revealFxSpine.spine.anchor.set(0.5, 0.5);
      this.spineObjects.set('reveal_fx', revealFxSpine);
      this.app.stage.addChild(revealFxSpine);
      if (this.buttonManager && this.buttonManager.uiContainer) {
        this.app.stage.addChild(this.buttonManager.uiContainer);
      }
      if (this.buttonManager && typeof this.buttonManager.ensureUIOnTop === 'function') {
        this.buttonManager.ensureUIOnTop();
      }
      // Get all available animations
      const animations = revealFxSpine.spine.state.data.skeletonData.animations.map(a => a.name);
      revealFxSpine.interactive = true;
      revealFxSpine.cursor = 'pointer';
      let playAnim = animations.includes('reveal') ? 'reveal' : animations[0];
      revealFxSpine.on('pointertap', () => {
        revealFxSpine.spine.state.setAnimation(0, playAnim, false);
        const listener = {
          complete: (trackEntry) => {
            if (trackEntry.animation.name === playAnim) {
              revealFxSpine.visible = false;
              revealFxSpine.spine.state.removeListener(listener);
            }
          }
        };
        revealFxSpine.spine.state.addListener(listener);
        revealFxSpine.interactive = false;
      });
    } catch (error) {
      console.error('Failed to create Reveal FX spine:', error);
    }
  }

  /**
   * Create reveal FX spine separately
   */
  createRevealFxSpine() {
    try {
      if (!this.spineLoader.isAssetLoaded('reveal_fx')) {
        console.error('Reveal FX Spine assets not loaded yet');
        return;
      }
      const revealFxSpine = new SpineObject(
        'reveal_fxSkeleton',
        'reveal_fxAtlas',
        this.app.screen.width / 2,
        this.app.screen.height / 2 + 200
      );
      if (!revealFxSpine.spine || !revealFxSpine.spine.skeleton) {
        console.error('Failed to create Reveal FX spine object');
        return;
      }
      revealFxSpine.spine.scale.set(0.5);
      revealFxSpine.spine.anchor && revealFxSpine.spine.anchor.set(0.5, 0.5);
      this.spineObjects.set('reveal_fx', revealFxSpine);
      this.app.stage.addChild(revealFxSpine);
      if (this.buttonManager && this.buttonManager.uiContainer) {
        this.app.stage.addChild(this.buttonManager.uiContainer);
      }
      if (this.buttonManager && typeof this.buttonManager.ensureUIOnTop === 'function') {
        this.buttonManager.ensureUIOnTop();
      }
      // Get all available animations
      const animations = revealFxSpine.spine.state.data.skeletonData.animations.map(a => a.name);
      revealFxSpine.interactive = true;
      revealFxSpine.cursor = 'pointer';
      let playAnim = animations.includes('reveal') ? 'reveal' : animations[0];
      revealFxSpine.on('pointertap', () => {
        revealFxSpine.spine.state.setAnimation(0, playAnim, false);
        const listener = {
          complete: (trackEntry) => {
            if (trackEntry.animation.name === playAnim) {
              revealFxSpine.visible = false;
              revealFxSpine.spine.state.removeListener(listener);
            }
          }
        };
        revealFxSpine.spine.state.addListener(listener);
        revealFxSpine.interactive = false;
      });
    } catch (error) {
      console.error('Failed to create Reveal FX spine:', error);
    }
  }

  /**
   * Play a specific spine animation
   */
  playSpineAnimation(spineName, animationName, loop = true) {
    const spineObject = this.spineObjects.get(spineName);
    if (spineObject) {
      spineObject.playAnimation(animationName, loop);
    } else {
      console.warn(`Spine object "${spineName}" not found`);
    }
  }

  /**
   * Get a spine object
   */
  getSpineObject(spineName) {
    return this.spineObjects.get(spineName);
  }

  centerReelFrame() {
    if (!this.reelFrame) return;
    this.reelFrame.anchor.set(0.5);
    this.reelFrame.x = this.app.screen.width / 2;
    this.reelFrame.y = this.app.screen.height / 2;
  }

  async loadUIBase(gameData) {
    const sheet = this.assetLoader.getSheet('buttons');
    if (sheet) {
      this.uiBase = PIXI.Sprite.from(sheet.textures['ui_base.png']);
      this.uiBase.anchor.set(0.5, 0);

      // Use the same mobile-responsive scaling as reel frame
      const screenWidth = this.app.screen.width;
      let scale = 0.6; // Default scale
      let yOffset = -90; // Default Y offset

      if (screenWidth <= 480) {
        scale = 0.24; // Small mobile - zoom out more
        yOffset = -5; // Move closer to reel frame for small mobile
      } else if (screenWidth <= 767) {
        scale = 0.40; // Regular mobile - zoom out
        yOffset = -60; // Move closer to reel frame for regular mobile
      } else if (screenWidth <= 1024) {
        scale = 0.5; // Tablet - slight zoom out
        yOffset = -75; // Move closer to reel frame for tablet
      } else {
        scale = 0.6; // Desktop - original scale
        yOffset = -90; // Original offset for desktop
      }

      this.uiBase.scale.set(scale);
      this.uiBase.x = this.reelFrame.x;
      this.uiBase.y = this.reelFrame.y + (this.reelFrame.height * this.reelFrame.scale.y) / 2 + yOffset;
      this.app.stage.addChild(this.uiBase);
      this.buttonManager = new UIButtonManager(this.app, sheet, this.assetLoader);
      this.app.buttonManager = this.buttonManager;
      this.buttonManager.addCornerButtons();
      this.buttonManager.addUIBaseButtons(this.uiBase, gameData);
      this.buttonManager.setCredits(this.credits);

      const isContinuationGame = gameData && gameData.scores && gameData.scores.length > 0;
      if (this.buttonManager && typeof this.buttonManager.setGameContinuationUI === 'function') {
        this.buttonManager.setGameContinuationUI(isContinuationGame); // Let setGameContinuationUI handle playClicked
      }
    }
  }

  onResize() {
    if (!this.app || !this.app.renderer) return;
    this.app.renderer.resize(window.innerWidth, window.innerHeight);

    if (this.bg) {
      this.bg.width = this.app.screen.width;
      this.bg.height = this.app.screen.height;
    }

    // Recalculate mobile-responsive scale for reel frame
    if (this.reelFrame) {
      const screenWidth = this.app.screen.width;
      let scale = 0.6; // Default scale
      if (screenWidth <= 480) {
        scale = 0.35; // Small mobile - zoom out more
      } else if (screenWidth <= 767) {
        scale = 0.45; // Regular mobile - zoom out
      } else if (screenWidth <= 1024) {
        scale = 0.5; // Tablet - slight zoom out
      } else {
        scale = 0.6; // Desktop - original scale
      }
      this.reelFrame.scale.set(scale);
      this.centerReelFrame();
    }

    // Recalculate mobile-responsive scale for UI base
    if (this.uiBase && this.reelFrame) {
      const screenWidth = this.app.screen.width;
      let scale = 0.6; // Default scale
      let yOffset = -90; // Default Y offset

      if (screenWidth <= 480) {
        scale = 0.24; // Small mobile - zoom out more
        yOffset = -5; // Move closer to reel frame for small mobile
      } else if (screenWidth <= 767) {
        scale = 0.40; // Regular mobile - zoom out
        yOffset = -60; // Move closer to reel frame for regular mobile
      } else if (screenWidth <= 1024) {
        scale = 0.5; // Tablet - slight zoom out
        yOffset = -75; // Move closer to reel frame for tablet
      }

      this.uiBase.scale.set(scale);
      this.uiBase.x = this.reelFrame.x;
      this.uiBase.y = this.reelFrame.y + (this.reelFrame.height * this.reelFrame.scale.y) / 2 + yOffset;
      if (this.buttonManager) {
        this.buttonManager.repositionButtons(this.uiBase);
      }
    }

    if (this.symbolManager) {
      this.symbolManager.repositionGrid(this.reelFrame.x, this.reelFrame.y);
    }

    this.updateSpinePositions();
  }

  /**
   * Update spine object positions on resize
   */
  updateSpinePositions() {
    this.spineObjects.forEach((spineObject, name) => {
      if (!spineObject || !spineObject.spine) return;

      if (name === 'bg') {
        const bounds = spineObject.spine.getLocalBounds();
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;

        const scaleX = screenWidth / bounds.width;
        const scaleY = screenHeight / bounds.height;
        const scale = Math.max(scaleX, scaleY);

        spineObject.spine.scale.set(scale);

        if (spineObject.spine.anchor) {
          spineObject.spine.anchor.set(0.5, 0.5);
          spineObject.x = screenWidth / 2;
          spineObject.y = screenHeight / 2;
        } else {
          const scaledWidth = bounds.width * scale;
          const scaledHeight = bounds.height * scale;
          spineObject.x = (screenWidth - scaledWidth) / 2 - bounds.x * scale;
          spineObject.y = (screenHeight - scaledHeight) / 2 - bounds.y * scale;
        }

      } else if (name === 'bigwin') {
        spineObject.x = this.app.screen.width / 2;
        spineObject.y = this.app.screen.height / 2 - 200;
      } else if (name === 'reveal_fx') {
        spineObject.x = this.app.screen.width / 2;
        spineObject.y = this.app.screen.height / 2 + 200;
      }
    });
  }

  /**
   * Start the game loop
   */
  startGameLoop() {
    this.gameLoop = () => {
      this.update();
      requestAnimationFrame(this.gameLoop);
    };
    this.gameLoop();
  }

  /**
   * Update method called in game loop
   */
  update() {
    this.spineObjects.forEach((spineObject, name) => {

    });
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.gameLoop) {
      this.gameLoop = null;
    }
    // Clean up spine objects
    this.spineObjects.forEach((spineObject) => {
      if (spineObject && spineObject.destroy) {
        spineObject.destroy();
      }
    });
    this.spineObjects.clear();
    this.spineLoader.unloadAllSpineAssets();
  }

  onAssetsLoaded() {
    // loade remaining assets
  }

  // Credits management
  getCredits() {
    return this.credits;
  }
  setCredits(amount) {
    this.credits = amount;
    if (this.buttonManager && typeof this.buttonManager.setCredits === 'function') {
      this.buttonManager.setCredits(this.credits);
    }
  }
  addCredits(amount) {
    this.setCredits(this.credits + amount);
  }
  deductCredits(amount) {
    this.setCredits(this.credits - amount);
  }

  restartGame() {
    // Remove all symbols from stage if present
    if (this.symbolManager && this.symbolManager.symbols) {
      this.symbolManager.symbols.forEach(container => {
        if (container.parent === this.app.stage) {
          this.app.stage.removeChild(container);
        }
      });
      this.symbolManager.symbols = [];
    }
    if (this.symbolManager) {
      this.symbolManager.createGrid();
      // Fetch scores again for a new game or pending bet
      getPendingBetScores().then(gameData => {
        if (gameData.wallet !== undefined && gameData.wallet !== null) {
          this.setCredits(gameData.wallet); // Update credits from pending bet API on restart
        }
        if(gameData.scores.length > 0) {
          this.symbolManager.addActualSymbols(gameData);
          if(this.buttonManager) {
            this.buttonManager.setGameContinuationUI(true);
          }
        } else {
          console.log("No pending bet scores found on restart. Waiting for ticket purchase.");
          // Optionally, you might want to clear existing symbols or prepare for new game state
          if(this.buttonManager) {
            this.buttonManager.setGameContinuationUI(false);
          }
        }
      }).catch(error => {
        console.error("Error fetching pending scores for restartGame:", error);
        // On error, we still don't want to call addActualSymbols with undefined.
        // The game should remain in a state ready for a ticket purchase.
        if(this.buttonManager) {
          this.buttonManager.setGameContinuationUI(false);
        }
      });
    }
    if (this.buttonManager && typeof this.buttonManager.resetGameUI === 'function') {
      this.buttonManager.resetGameUI();
    }
    if (this.buttonManager && typeof this.buttonManager.ensureUIOnTop === 'function') {
      this.buttonManager.ensureUIOnTop();
    }
    if (this.buttonManager && this.buttonManager.uiContainer) {
      this.app.stage.addChild(this.buttonManager.uiContainer);
    }
  }
  /**
   * Show the big win animation in the center of the screen with the amount, hide after 3 seconds
   */
  showBigWin(amount) {
    if (this.spineObjects.has('bigwin')) {
      const old = this.spineObjects.get('bigwin');
      if (old.parent) old.parent.removeChild(old);
      this.spineObjects.delete('bigwin');
    }
    if (this.bigWinAmountText && this.bigWinAmountText.parent) {
      this.bigWinAmountText.parent.removeChild(this.bigWinAmountText);
      this.bigWinAmountText = null;
    }
    const screenWidth = this.app.screen.width;
    const screenHeight = this.app.screen.height;
    const bigwinSpine = new SpineObject('bigwinSkeleton', 'bigwinAtlas', 0, 0);
    bigwinSpine.x = screenWidth / 2;
    bigwinSpine.y = screenHeight / 2;
    bigwinSpine.spine.x = 0;
    bigwinSpine.spine.y = 0;

    let bigWinScale = 0.7
    if (screenWidth <= 480) {
      bigWinScale = 0.35; // Smaller for small mobile
    } else if (screenWidth <= 767) {
      bigWinScale = 0.45; // Smaller for regular mobile
    }
    bigwinSpine.spine.scale.set(bigWinScale);

    if (bigwinSpine.spine.anchor) bigwinSpine.spine.anchor.set(0.5, 0.5);
    const anims = bigwinSpine.spine.state.data.skeletonData.animations.map(a => a.name);
    if (anims.length > 0) {
      bigwinSpine.spine.state.setAnimation(0, anims[0], false);
      if (soundManager && typeof soundManager.playBigWin === 'function') {
        soundManager.playBigWin();
      }
    }
    this.spineObjects.set('bigwin', bigwinSpine);
    this.app.stage.addChild(bigwinSpine);
    const style = new PIXI.TextStyle({
      align: 'center',
      dropShadow: true,
      dropShadowAngle: 1.5,
      dropShadowDistance: 2,
      fill: '#ffd700',
      fontFamily: 'TCCEB',
      fontSize: 64,
      fontWeight: 'bolder',
      stroke: '#3a4124',
      strokeThickness: 6,
      whiteSpace: 'normal',
    });
    this.bigWinAmountText = new PIXI.Text(`₦${amount}`, style);
    this.bigWinAmountText.anchor.set(0.5);
    this.bigWinAmountText.x = screenWidth / 2;
    this.bigWinAmountText.y = screenHeight / 2 + 30;
    this.app.stage.addChild(this.bigWinAmountText);
    // Re-add UI container to top
    if (this.buttonManager && this.buttonManager.uiContainer) {
      this.app.stage.addChild(this.buttonManager.uiContainer);
    }
    // Hide after 5 seconds
    setTimeout(() => {
      if (bigwinSpine.parent) bigwinSpine.parent.removeChild(bigwinSpine);
      if (this.bigWinAmountText && this.bigWinAmountText.parent) {
        this.bigWinAmountText.parent.removeChild(this.bigWinAmountText);
        this.bigWinAmountText = null;
      }
      this.spineObjects.delete('bigwin');
    }, 5000);
  }

} 